import type { Role, WorkingHours } from '@/api/types';
import { Badge, Checkbox } from '@/components/ui';
import { humanize } from '@/lib/format';
import { ROLE_TONE } from './helpers';

export function RoleBadge({ role }: { role: Role }) {
  return <Badge tone={ROLE_TONE[role]}>{humanize(role)}</Badge>;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Weekly working hours (IST) — used by routing to only assign during shifts. */
export function WorkingHoursEditor({
  value,
  onChange,
}: {
  value: WorkingHours;
  onChange: (v: WorkingHours) => void;
}) {
  const byDay = new Map(value.days.map((d) => [d.day, d]));
  const set = (day: number, patch: { enabled?: boolean; start?: string; end?: string }) => {
    const cur = byDay.get(day) ?? { day, start: '10:00', end: '19:00' };
    const enabled = patch.enabled ?? byDay.has(day);
    const others = value.days.filter((d) => d.day !== day);
    const days = enabled ? [...others, { ...cur, ...patch, day }] : others;
    onChange({
      ...value,
      days: days.map(({ day: d, start, end }) => ({ day: d, start, end })).sort((a, b) => a.day - b.day),
    });
  };

  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-sm font-medium text-slate-700">Working hours (IST)</legend>
      {[1, 2, 3, 4, 5, 6, 0].map((day) => {
        const d = byDay.get(day);
        return (
          <div
            key={day}
            className="grid max-w-md grid-cols-[3.5rem_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5 sm:grid-cols-[4.5rem_minmax(0,1fr)_auto_minmax(0,1fr)] sm:gap-3"
          >
            <Checkbox
              label={DAYS[day]}
              checked={!!d}
              onChange={(e) => set(day, { enabled: e.target.checked })}
            />
            <input
              type="time"
              aria-label={`${DAYS[day]} start`}
              disabled={!d}
              value={d?.start ?? '10:00'}
              onChange={(e) => set(day, { start: e.target.value })}
              className="h-9 w-full min-w-0 rounded-lg border border-slate-200 px-1.5 text-sm disabled:opacity-40"
            />
            <span className="text-slate-400">–</span>
            <input
              type="time"
              aria-label={`${DAYS[day]} end`}
              disabled={!d}
              value={d?.end ?? '19:00'}
              onChange={(e) => set(day, { end: e.target.value })}
              className="h-9 w-full min-w-0 rounded-lg border border-slate-200 px-1.5 text-sm disabled:opacity-40"
            />
          </div>
        );
      })}
    </fieldset>
  );
}
